import { ref } from "vue";
import { api } from "../api";

/**
 * Runtime whitelabel theming.
 *
 * This is what makes ONE player deployment serve every agent. The site asks the
 * API who it is — resolved server-side from the request's hostname, never from
 * anything the client chooses — and paints itself accordingly.
 *
 * The alternative, a build per whitelabel, means N deployments to patch for
 * every security fix, and one of them eventually gets missed. Here, onboarding a
 * new agent is a database row and a DNS record.
 */
const branding = ref(null);
const siteName = ref("");
const loaded = ref(false);
const unavailable = ref(false);

export function useBranding() {
  async function load() {
    if (loaded.value) return;

    try {
      const { data } = await api.client.get("/site/branding");

      siteName.value = data.siteName;
      branding.value = data.branding ?? {};
      apply(branding.value);

      // index.html ships "Loading…" because the markup is shared by every
      // brand. This is where the site gets its real name — in the tab, in the
      // bookmark, and in the history entry.
      //
      // The document language is NOT set here: createI18n already sets it from
      // the resolved locale at startup, and setLocale keeps it current.
      document.title = data.siteName || "Casino";
    } catch (err) {
      // 404 = unknown hostname, 503 = whitelabel suspended. Either way the site
      // must not silently fall back to a generic skin and keep taking
      // registrations under a brand that is not trading.
      unavailable.value = true;
    } finally {
      loaded.value = true;
    }
  }

  return { branding, siteName, loaded, unavailable, load };
}

/**
 * Writes the agent's tokens onto the CSS custom properties the stylesheet reads.
 *
 * Only known keys are applied, and each is validated. BrandConfig is
 * operator-authored and reaches the browser unverified, so injecting it straight
 * into a style attribute would be a CSS injection surface.
 */
function apply(tokens) {
  const root = document.documentElement;

  const map = {
    primary: "--accent",
    primaryText: "--accent-fg",
    surface: "--surface",
    surfaceMuted: "--surface-muted",
    text: "--content",
    textMuted: "--content-muted",
  };

  for (const [key, cssVar] of Object.entries(map)) {
    const value = tokens?.[key];
    if (typeof value !== "string") continue;

    const rgb = toRgbTriplet(value);
    if (rgb) root.style.setProperty(cssVar, rgb);
  }
}

/**
 * Accepts "#rrggbb" or "r g b" and returns the "r g b" form Tailwind's
 * <alpha-value> syntax needs. Anything else is rejected rather than passed
 * through — that rejection is the injection guard.
 */
function toRgbTriplet(value) {
  const hex = /^#?([0-9a-f]{6})$/i.exec(value.trim());
  if (hex) {
    const n = parseInt(hex[1], 16);
    return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
  }

  const triplet = /^(\d{1,3})\s+(\d{1,3})\s+(\d{1,3})$/.exec(value.trim());
  if (triplet && triplet.slice(1).every((n) => +n <= 255)) {
    return `${triplet[1]} ${triplet[2]} ${triplet[3]}`;
  }

  return null;
}
